import { createLogger } from '@svs/nova-core/log';
import { createCoupon, deleteCoupon, updateCouponOutcome } from '@svs/nova-poolbetting/core/frameworks/actions/coupon';
import type { ActionResult } from '@svs/nova-poolbetting/core/lib/server';
import {
  type Bet,
  type CouponId,
  type CouponIndex,
  type CouponType,
  getCouponIndex,
} from '@svs/nova-poolbetting/schema/coupon';

const logger = createLogger('SheetUtils');

/**
 * Updates SROW coupons with quick pick selections.
 * Deletes, updates, or creates coupons based on the provided outcomes.
 *
 * @param root0 function args
 * @param root0.payloadOutcomes - Multi-dimensional array of outcomes for each coupon
 * @param root0.couponIndices - Array of existing coupon indices
 * @param root0.couponId - The coupon ID to update
 * @param root0.couponType - The type of coupon being updated
 * @param root0.boardIndex - The board index for the outcomes
 * @returns Promise with status and optional error
 */
export async function updateSrowCouponsWithQuickPick({
  payloadOutcomes,
  couponIndices,
  couponId,
  couponType,
  boardIndex,
}: {
  payloadOutcomes: Bet[];
  couponIndices: CouponIndex[];
  couponId: CouponId;
  couponType: CouponType;
  boardIndex: number;
}): Promise<ActionResult<void>> {
  try {
    const shouldDeleteCoupons = payloadOutcomes.length < couponIndices.length;

    // Wait for deletions to complete
    if (shouldDeleteCoupons) {
      const deletePromises = [];
      for (let i = couponIndices.length; i > payloadOutcomes.length; i--) {
        deletePromises.push(
          deleteCoupon({ couponId, couponIndex: couponIndices[i - 1] as CouponIndex }).catch((error: unknown) => {
            logger.error('Deleting coupon failed', { error, couponId, couponIndex: couponIndices[i - 1] });
          })
        );
      }
      await Promise.all(deletePromises);
    }

    // Wait for updates and creations to complete
    const updatePromises = payloadOutcomes.map(async (outcomes, index) => {
      if (getCouponIndex(index) && couponIndices.includes(index)) {
        const couponIndex = index;
        await updateCouponOutcome(outcomes, { couponId, couponIndex, boardIndex }, 'keep').catch((error: unknown) => {
          logger.error('Updating coupon outcomes failed', {
            error,
            couponId,
            couponIndex,
            boardIndex,
          });
        });
      } else {
        // We do it this way because we cannot use PIX and then add server-side, since we don't have access to the Zustand store there.
        const response = await createCoupon(couponType, { couponId });
        if (response.status === 'SUCCESS') {
          await updateCouponOutcome(
            outcomes,
            { couponId: response.data.couponId, couponIndex: response.data.couponIndex, boardIndex },
            'keep'
          ).catch((error: unknown) => {
            logger.error('Updating coupon outcomes failed', {
              error,
              couponId: response.data.couponId,
              couponIndex: response.data.couponIndex,
              boardIndex,
            });
          });
        } else {
          logger.error('Creating coupon failed', {
            couponId,
            requestedIndex: index,
          });
        }
      }
    });

    await Promise.all(updatePromises);
    return { status: 'SUCCESS', data: undefined };
  } catch {
    return { status: 'ERROR', error: 'Updating SROW coupons with quick pick failed' };
  }
}
